import React from 'react';
import cx from 'classnames';
import { useSwipeable } from 'react-swipeable';
import 'client/styles/tabItems.scss';
import TabItem from './tabItem';

type TrackingTabState = {
  currentTab: string,
}

const defaultProps = {
  children: [],
  className: '',
  onClick: () => {
    // do nothing
  },
  showDots: false,
  defaultTab: 0,
};

type Props = {
  children: React.ReactNode;
  className: string;
  onClick: (options: TrackingTabState) => void;
  showDots: boolean;
  defaultTab: number;
};

const ENTER_KEY = 'Enter';

const Tabs = (props: Props) => {
  const { children, className, onClick, showDots, defaultTab } = props;

  const [activeTab, setActiveTab] = React.useState(defaultTab);

  const numItems = React.Children.count(props.children);

  const mapTab: Array<string> = React.Children.toArray(children).reduce(
    (acc: Array<string>, child) => {
      if (!React.isValidElement(child)) return acc;

      const { label } = child.props;
      return [
        ...acc,
        label,
      ];
    }, [],
  );

  const MAX_INDEX = numItems - 1;

  const handlers = useSwipeable({
    onSwipedRight: () => { setActiveTab(activeTab === 0 ? MAX_INDEX : activeTab - 1); },
    onSwipedLeft: () => { setActiveTab(activeTab === MAX_INDEX ? 0 : activeTab + 1); },
    swipeDuration: 500,
    preventScrollOnSwipe: true,
    trackMouse: true,
  });

  const onClickTabItem = (position: number) => {
    if (position !== activeTab) {
      setActiveTab(position);
      onClick({ currentTab: mapTab[position] });
    }
  };

  if (numItems === 0) return null;

  return (
    <div className={cx('tabs', className)}>
      <ul className="tab-list">
        {React.Children.map(children, (child, index) => {
          if (!React.isValidElement(child)) return null;
          const { label } = child.props;
          return (
            <TabItem
              activeTab={activeTab}
              key={label}
              label={label}
              position={index}
              onClick={onClickTabItem} />
          );
        })}
      </ul>

      {activeTab !== undefined && (
        <div className="tab-content" {...handlers}>
          <div className="full-width-background" />
          {React.Children.map(children, (child, index) => {
            if (index !== activeTab || !React.isValidElement(child)) return null;
            return child.props.children;
          })}

          {showDots && (
            <div className="tab-dots">
              {React.Children.map(children, (child, index) => (
                <div
                  className={cx('tab-dot', {
                    'tab-dot-active': activeTab === index,
                  })}
                  onClick={() => onClickTabItem(index)}
                  onKeyDown={event =>
                    event.key === ENTER_KEY && onClickTabItem(index)
                  } />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

Tabs.defaultProps = defaultProps;

export default Tabs;
